import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Typography } from "@mui/material"
import { toast } from "react-toastify"

import MetricAPI from "../../../api/metric"

import { setLoading, setOpenSidebar } from "../../redux/slices/appSlice"

import {
  setMaevaGroups,
  setMaevaPractices,
  setMaevaRegions,
  setMaevaSelectedPractices,
  setMaevaSelectedRegions,
  setMetricOpportunityPractices,
  setMetricOpportunityRegions,
  setMetricOpportunitySelectedPractice,
  setMetricOpportunitySelectedRegion,
  setMetricOpportunityTabs,
  setOpportunityAdvisorList,
  setSelectedMetricOpportunityTab,
} from "../../redux/slices/metricSlice"

import MetricOpportunityHeader from "../../components/Headers/MetricOpportunityHeader"
import MetricOpportunityTabs from "../../components/Tabs/MetricOpportunityTabs"
import { MetricOpportunityTable } from "../../components/Table"

import OpportunityModal from "../../components/Modal/OpportunityModal"

import { clearLocalstorage, getTokenLocal } from "../../../helper/utils"

import { MetricOpportunityContainer, NoGroupWrapper } from "./Atom"

import { useResponseHandle } from "../../hooks"
import AdminAPI from "../../../api/admin"
import { metricStoreNestedDataDummy } from "../../../config/constants/dummyData"

const metricAPI = new MetricAPI()
const adminAPI = new AdminAPI()

const MetricOpportunity = () => {
  const token: any = getTokenLocal()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  dispatch(setOpenSidebar(false))

  const opportunityData = useSelector((state: any) => {
    return state.metric.metricOpportunityData
  })

  const maevaSelectedGroup = useSelector((state: any) => {
    return state.metric.maevaSelectedGroupsNoAll?.[0]
  })
  const maevaSelectedRegion = useSelector((state: any) => {
    return state.metric.maevaSelectedRegions?.[0]
  })

  const fetchAllMetricsAssignedToGroup = async () => {
    dispatch(setLoading(true))
    if (token) {
      const response: any = await metricAPI.getAllOpportunityMetrics(
        token,
        maevaSelectedGroup?.group_sk,
      )
      if (
        response?.data?.status_code != 200 &&
        response?.data?.status_code != 401
      ) {
        toast.error(response?.data?.display_message)
      } else {
        dispatch(setLoading(false))
        return response?.data?.data
      }
    }

    dispatch(setLoading(false))
  }

  const fetchAssignedBucketsToGroup = async () => {
    dispatch(setLoading(true))

    if (token && maevaSelectedGroup?.group_sk) {
      const response: any = await metricAPI.getAssignedBucketsToGroup(
        token,
        maevaSelectedGroup?.group_sk,
      )
      if (
        response?.data?.status_code != 200 &&
        response?.data?.status_code != 401
      ) {
        toast.error(response?.data?.display_message)
      } else {
        // const allMetrics = await fetchAllMetricsAssignedToGroup()

        const allBucket: any = {
          metric_bucket: "All",
          metric_bucket_id: 0,
          metrics: [],
          columnHeaders: [],
          index: 0,
        }

        dispatch(
          setMetricOpportunityTabs([{ ...allBucket }, ...response?.data?.data]),
        )
        dispatch(setLoading(false))
        return response.data?.data
      }
    }

    dispatch(setLoading(false))
  }

  const fetchGroupList = async () => {
    dispatch(setLoading(true))
    const response = await metricAPI.getMetricGroupsBoarded(token)

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        const groups: Array<Object> = []
        result?.data?.forEach((group: any) => {
          groups.push({ ...group, label: group.group_name })
        })
        dispatch(setMaevaGroups(groups))
        dispatch(setLoading(false))
        return groups
      }
    } else {
      clearLocalstorage()
      navigate("/auth")
    }

    dispatch(setLoading(false))

    return result.data
  }

  const fetchRegionList = async () => {
    dispatch(setLoading(true))

    const response = await metricAPI.getMetricRegionsByGroups(token, [
      maevaSelectedGroup.group_sk,
    ])

    const result: any = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        const regions: Array<Object> = [
          { label: "All", region: "all", name: "all" },
        ]

        result?.data?.regions?.forEach((region: any) => {
          regions.push({
            label: region,
            region: region.toLowerCase(),
            name: region,
          })
        })

        dispatch(setMaevaRegions(regions))
      }
    } else {
      clearLocalstorage()
      navigate("/auth")
    }

    dispatch(setLoading(false))
    return result.data
  }

  const fetchPracticeList = async () => {
    dispatch(setLoading(true))

    const response = await metricAPI.getMetricPracticesByRegions(
      token,
      [maevaSelectedGroup?.group_sk],
      [maevaSelectedRegion?.name],
    )

    const result: any = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        const practices: Array<Object> = [{ label: "All", name: "all" }]

        result?.data?.forEach((practice: any) => {
          practices.push({ ...practice, label: practice?.name })
        })

        dispatch(setMaevaPractices(practices))
      }
    } else {
      clearLocalstorage()
      navigate("/auth")
    }

    dispatch(setLoading(false))
    return result.data
  }

  const fetchAdvisorList = async () => {
    const advisor: Array<Object> = [{ label: "All", uid: "all" }]
    const response = await adminAPI.getAllAdvisors(
      token,
      maevaSelectedGroup?.group_sk,
    )

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        result?.data?.forEach((g: any) => {
          advisor.push({
            ...g,
            label: `${g.name}`,
          })
        })

        dispatch(setOpportunityAdvisorList(advisor))
      }
    } else {
      navigate("/auth")
    }

    return result
  }

  const fetchOpportunityByBucket = async (bucketId: any, newValue?: any) => {
    dispatch(setLoading(true))

    const response = await metricAPI.getMetricGroupData(
      token,
      maevaSelectedGroup?.group_sk,
      bucketId,
    )

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    const setDynamicReferenceOrder = (response: any): string[] => {
      const columnHeaders = response.columnHeaders
      return columnHeaders.map((col: any) => col.column_id) // Extract dynamic order
    }

    const sortDynamicColumns = (
      response: any,
    ): any["selectedMetricOpportunityTab"]["columnHeaders"] => {
      const columnHeaders = response.columnHeaders
      const dynamicOrder = setDynamicReferenceOrder(response) // Get the dynamic order
      return columnHeaders.sort(
        (a: any, b: any) =>
          dynamicOrder.indexOf(a.column_id) - dynamicOrder.indexOf(b.column_id),
      )
    }

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        const sortedColumnHeaders = sortDynamicColumns(result?.data)

        dispatch(
          setSelectedMetricOpportunityTab({
            ...result?.data,
            index: newValue,
            columnHeaders: sortedColumnHeaders,
          }),
        )
      }
    } else {
      clearLocalstorage()
      navigate("/auth")
    }
    dispatch(setLoading(false))
    return result?.data
  }

  const fetchNestedData = async (
    bucketId: any,
    metricId?: any,
    level?: number,
    region?: any,
  ) => {
    let response = null

    if (level == 0) {
      response = await metricAPI.getMetricRegionData(
        token,
        maevaSelectedGroup?.group_sk,
        metricId,
        maevaSelectedRegion?.name
      )
    } else if (level == 1) {
      response = await metricAPI.getMetricPracticeData(
        token,
        maevaSelectedGroup?.group_sk,
        metricId,
        region,
      )
    }

    const result = useResponseHandle({
      apiResponse: response,
      successMessage: false,
    })

    if (result?.isAuthorized) {
      if (result?.statusCode == 200) {
        return result?.data
      }
    } else {
      clearLocalstorage()
      navigate("/auth")
    }
    return result?.data
  }

  useEffect(() => {
    fetchGroupList()
  }, [])

  useEffect(() => {
    if (maevaSelectedGroup) {
      fetchAdvisorList()
    }
  }, [maevaSelectedGroup])

  useEffect(() => {
    if (maevaSelectedGroup) {
      fetchAssignedBucketsToGroup()
    }
  }, [maevaSelectedGroup])

  useEffect(() => {
    if (maevaSelectedGroup) {
      fetchRegionList()
    }
  }, [maevaSelectedGroup])

  useEffect(() => {
    if (maevaSelectedRegion) {
      fetchPracticeList()
    }
  }, [maevaSelectedRegion])

  return (
    <MetricOpportunityContainer>
      <OpportunityModal
        fetchOpportunityByBucket={fetchOpportunityByBucket}
        // fetchAllMetricsAssignedToGroup={fetchAllMetricsAssignedToGroup}
      />
      <MetricOpportunityHeader name="Metrics" />
      {maevaSelectedGroup ? (
        <>
          <MetricOpportunityTabs
            // fetchAllMetricsAssignedToGroup={fetchAllMetricsAssignedToGroup}
            fetchOpportunityByBucket={fetchOpportunityByBucket}
          />
          <MetricOpportunityTable
            opportunityData={opportunityData}
            fetchNestedData={fetchNestedData}
          />
        </>
      ) : (
        <NoGroupWrapper>
          <Typography>Select A Group</Typography>
        </NoGroupWrapper>
      )}
    </MetricOpportunityContainer>
  )
}

export default MetricOpportunity
